import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

// 종료 알림(notifications) wire 계약 경계.
//
// 상류는 **세 단계를 모두 통과해야** 통지를 보낸다. 이 파일은 이 세 단계를 다룬다:
//   1. 실행 정책. 제출 payload 의 `notifications` 다. **필드 부재 = 위임(inherit)** 이다.
//      `mode="enabled"` 는 아래 2가 꺼져 있어도 이 실행을 통지한다. `"disabled"` 는
//      통지를 만들지 않는다.
//   2. workspace 기본 설정. `default_enabled` 이고 **기본값이 false** 다.
//   3. 수신자. 상류 workspace 멤버 전원과 이메일로 등록된 recipient 전원이다.
//
// "나" 는 **알림 수신 이메일**(`/v1/me` 의 `notification_email`, 사용자가 고른 값)로 식별한다.
// 로그인 이메일이 아니다. 로그인 이메일은 GitHub noreply 주소일 수 있다. 그러면 Slack 이
// 사용자를 찾지 못한다. 수신 이메일이 없으면(null) 수신자가 될 수 없다. 그래서 토글과 제출
// 시드가 모두 꺼진다.
//
// 그래서 "내가 알림을 받는다"는 1과 3을 함께 켜야 성립한다. 한쪽만 켜면 경고 없이 아무 일도
// 일어나지 않는다:
//   - 1만 켜면 통지는 나간다. 그러나 내 이메일이 수신자에 없어서 나에게 오지 않는다.
//   - 3만 켜면 2가 꺼져 있어서 통지가 만들어지지 않는다.
// `setSelfNotification` 이 두 가지를 한 번에 처리한다. 제출 payload 는
// `buildNotificationOverride` 로만 만든다.
//
// `status_categories` 는 `null`(키 부재)이면 workspace 기본 범주에 위임한다. 배열이면 범주를
// 고정한다. **빈 배열은 상류가 422 로 거절한다.** 그래서 이 경계는 빈 배열을 만들지 않는다.
// 이 앱에는 아직 범주·상세 수준을 고르는 화면이 없다. 그래서 두 키를 보내지 않는다.
// 위임하면 workspace 기본값이 적용된다.
//
// Owns:
// - 알림 wire 타입과 `mode` 3-상태·필드 부재의 의미.
// - 내 수신자 판정(`findSelfRecipient`·`isSelfNotificationOn`).
// - 켜고 끄는 invariant. 켤 때 행이 없으면 등록하고, 있으면 승격한다. **끌 때는 삭제하지 않고
//   강등**한다. 그래야 다시 켜기와 `last_error` 이력이 남는다.
// - 수신 이메일을 바꿀 때 옛 이메일의 내 행을 새 이메일로 옮기는 것(`moveSelfRecipient`).
//   지우고 다시 만들면 켜짐과 `last_error` 이력이 사라진다. 그대로 두면 옛 행이 계속 실패한다.
// - 제출 payload 조립. 끄면 키를 보내지 않는다.
//
// Does not own:
// - 누가 수신자가 되는지의 최종 판정과 Slack 사용자 해석. training-infra 가 소유한다.
// - workspace 기본 설정(`default_enabled`·대상·범주·상세 수준) 편집 화면.
// - 대상 workspace 선택. 화면이 고르지 않고 내 스코프에서 도출한다(docs/rules.md §15).
// - 수신 이메일 자체의 저장. `me.updateCurrentMeNotificationEmail` 이 소유한다.

// 상류 어휘를 그대로 쓴다. 종단 상태를 묶은 세 범주다.
export type NotificationStatusCategory = "success" | "failure" | "cancelled";

export type NotificationDetailLevel = "summary" | "failure_details";

// 실행 1건의 알림 정책. 값의 의미는 파일 머리 주석에 있다.
export type NotificationMode = "inherit" | "enabled" | "disabled";

export type NotificationOverride = {
  mode: NotificationMode;
  status_categories?: NotificationStatusCategory[];
  detail_level?: NotificationDetailLevel;
};

// workspace 기본 설정. 실행이 위임(inherit)일 때 적용되는 값이다.
export type TrainingNotificationSettingView = {
  default_enabled: boolean;
  default_target_type: string;
  default_slack_channel_id: string | null;
  default_status_categories: string[];
  default_detail_level: string;
};

// 등록된 수신자 한 명. `last_error` 는 마지막 전송 실패 사유다. 통지가 오지 않는 이유는
// 이 값에서만 읽을 수 있다. 대표적인 경우는 이메일로 Slack 사용자를 찾지 못한 경우다.
export type TrainingNotificationRecipientView = {
  id: string;
  email: string;
  name: string | null;
  enabled: boolean;
  status_categories: string[];
  detail_level: string;
  last_error: string | null;
};

// 조회·수정·수신자 등록은 모두 이 전체 상태를 돌려준다(부분 응답 없음).
export type TrainingNotificationView = {
  workspace_id: string;
  setting: TrainingNotificationSettingView;
  recipients: TrainingNotificationRecipientView[];
};

/**
 * 제출 payload 의 `notifications` 값.
 *
 * 끄면 `undefined` 라 **키가 payload 에 없다**(위임). `mode="disabled"` 를 보내지 않는 이유는
 * 수신자가 그 workspace 전체이기 때문이다. 내 토글이 다른 사람의 수신까지 끄면 안 된다.
 *
 * Args:
 *   on: 내 알림 토글 상태.
 *
 * Returns:
 *   켜져 있으면 `{ mode: "enabled" }`, 꺼져 있으면 `undefined`.
 */
export function buildNotificationOverride(on: boolean): NotificationOverride | undefined {
  return on ? { mode: "enabled" } : undefined;
}

/**
 * 내 이메일로 등록된 수신자 행.
 *
 * 이메일이 식별자라 workspace 당 최대 하나다. 대소문자는 무시한다. 저장된 수신 이메일과
 * 등록 이메일의 대소문자가 달라도 같은 사람이다.
 *
 * Args:
 *   view: 알림 전체 상태.
 *   email: 내 알림 수신 이메일.
 *
 * Returns:
 *   수신자 행. 없으면 null.
 */
export function findSelfRecipient(
  view: TrainingNotificationView,
  email: string,
): TrainingNotificationRecipientView | null {
  const normalized = email.trim().toLowerCase();
  return view.recipients.find((row) => row.email.trim().toLowerCase() === normalized) ?? null;
}

/**
 * 내 알림 토글의 현재 값. 수신자로 등록돼 있고 켜져 있는지 판정한다.
 *
 * 등록이 없는 것과 등록됐지만 꺼진 것은 화면에서 같은 OFF 다. 그러나 켤 때는 등록과 승격으로
 * 나뉜다. 그래서 그 판정은 `planSelfNotificationChange` 가 따로 한다.
 */
export function isSelfNotificationOn(view: TrainingNotificationView, email: string): boolean {
  return findSelfRecipient(view, email)?.enabled ?? false;
}

// 켜고 끌 때 실제로 부를 호출. `noop` 은 이미 원하는 상태라 왕복이 필요 없다는 뜻이다.
export type SelfNotificationChange =
  | { kind: "create" }
  | { kind: "update"; recipientId: string }
  | { kind: "noop" };

/**
 * 토글 변경으로 호출 계획을 만든다.
 *
 * 켜기는 수신자가 없으면 등록하고, 있으면 `enabled=true` 로 승격한다. 끄기는 **삭제하지 않고**
 * `enabled=false` 로 강등한다. 지우면 다시 켤 때 등록부터 다시 해야 하고 `last_error` 이력도
 * 사라진다. 수신자가 없는데 끄면 아무것도 하지 않는다.
 *
 * Args:
 *   view: 알림 전체 상태.
 *   email: 내 알림 수신 이메일.
 *   on: 바꾸려는 값.
 *
 * Returns:
 *   호출 계획.
 */
export function planSelfNotificationChange(
  view: TrainingNotificationView,
  email: string,
  on: boolean,
): SelfNotificationChange {
  const recipient = findSelfRecipient(view, email);
  if (recipient === null) {
    return on ? { kind: "create" } : { kind: "noop" };
  }
  return recipient.enabled === on
    ? { kind: "noop" }
    : { kind: "update", recipientId: recipient.id };
}

export async function getWorkspaceNotifications(
  workspaceId: string,
): Promise<TrainingNotificationView> {
  const response = await apiClient.get<TrainingNotificationView>(
    apiRoutes.training.workspaceNotifications(workspaceId),
  );
  return response.data;
}

// 수신자 등록(201). 이름은 표시용이라 없으면 보내지 않는다.
export async function addNotificationRecipient(
  workspaceId: string,
  request: { email: string; name?: string | null },
): Promise<TrainingNotificationView> {
  const name = request.name?.trim();
  const response = await apiClient.post<TrainingNotificationView>(
    apiRoutes.training.workspaceNotificationRecipients(workspaceId),
    {
      email: request.email,
      ...(name === undefined || name === "" ? {} : { name }),
      enabled: true,
    },
  );
  return response.data;
}

// 서버는 보낸 필드만 적용한다. 이 앱은 `enabled` 와 `email` 만 각각 따로 보낸다. 범주·상세
// 수준을 고르는 화면이 없다. 그래서 다른 축을 보내면 사용자가 정하지 않은 값이 저장된다.
export async function updateNotificationRecipientEnabled(
  workspaceId: string,
  recipientId: string,
  enabled: boolean,
): Promise<TrainingNotificationView> {
  const response = await apiClient.patch<TrainingNotificationView>(
    apiRoutes.training.workspaceNotificationRecipient(workspaceId, recipientId),
    { enabled },
  );
  return response.data;
}

export async function updateNotificationRecipientEmail(
  workspaceId: string,
  recipientId: string,
  email: string,
): Promise<TrainingNotificationView> {
  const response = await apiClient.patch<TrainingNotificationView>(
    apiRoutes.training.workspaceNotificationRecipient(workspaceId, recipientId),
    { email },
  );
  return response.data;
}

/**
 * 수신 이메일을 바꾼 뒤, 옛 이메일로 등록된 내 행을 새 이메일로 옮긴다.
 *
 * 다음 두 경우에는 아무것도 호출하지 않는다(왕복 0):
 * - 옛 행이 없으면 옮길 것이 없다.
 * - 새 이메일의 행이 이미 있으면 그 행이 나다. 그래서 옛 행을 바꾸지 않는다. 같은 workspace
 *   에 같은 이메일은 하나뿐이라, 옮기면 409 가 온다.
 *
 * Args:
 *   workspaceId: 내 스코프에서 도출한 workspace id.
 *   view: 방금 읽은 알림 전체 상태.
 *   fromEmail: 바꾸기 전 수신 이메일.
 *   toEmail: 저장된 새 수신 이메일.
 *
 * Returns:
 *   수정 후 전체 상태(호출이 없었으면 받은 상태 그대로).
 */
export async function moveSelfRecipient(
  workspaceId: string,
  view: TrainingNotificationView,
  fromEmail: string,
  toEmail: string,
): Promise<TrainingNotificationView> {
  const previous = findSelfRecipient(view, fromEmail);
  if (previous === null || findSelfRecipient(view, toEmail) !== null) {
    return view;
  }
  return updateNotificationRecipientEmail(workspaceId, previous.id, toEmail);
}

/**
 * 내 알림을 켜거나 끈다. 수신자 축을 수정하는 유일한 경로다.
 *
 * 이미 원하는 상태면 아무 호출도 하지 않고 받은 상태를 그대로 돌려준다(왕복 0).
 *
 * Args:
 *   workspaceId: 내 스코프에서 도출한 workspace id.
 *   view: 방금 읽은 알림 전체 상태.
 *   self: 내 알림 수신 이메일과 표시 이름.
 *   on: 바꾸려는 값.
 *
 * Returns:
 *   수정 후 전체 상태(호출이 없었으면 받은 상태 그대로).
 */
export async function setSelfNotification(
  workspaceId: string,
  view: TrainingNotificationView,
  self: { email: string; displayName?: string | null },
  on: boolean,
): Promise<TrainingNotificationView> {
  const change = planSelfNotificationChange(view, self.email, on);
  if (change.kind === "noop") {
    return view;
  }
  if (change.kind === "create") {
    return addNotificationRecipient(workspaceId, { email: self.email, name: self.displayName });
  }
  return updateNotificationRecipientEnabled(workspaceId, change.recipientId, on);
}
